import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Card, CardContent, Grid } from '@mui/material';
import { 
  CheckCircle, 
  Email, 
  Schedule, 
  Support,
  Dashboard 
} from '@mui/icons-material';
import { StyledButton } from '../../components/host/OnboardingComponents';
import styled from 'styled-components';

const RootContainer = styled(Box)`
  // min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #181e25;
  // padding: 2rem;
`;

const SuccessCard = styled(Card)`
  background: #232933;
  border-radius: 24px;
  max-width: 100%;
  width: 100%;
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.37);
`;

const HostOnboardingSuccess = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Clear onboarding data from localStorage after a delay
    // Keep it briefly in case user needs to reference it
    setTimeout(() => {
      localStorage.removeItem('hostOnboardingStep1');
      localStorage.removeItem('hostOnboardingStep2');
      localStorage.removeItem('hostOnboardingStep3');
      localStorage.removeItem('hostOnboardingStep4');
      localStorage.removeItem('hostApplicationResponse');
    }, 5000); // Clear after 5 seconds
  }, []);

  const handleGoToDashboard = () => {
    navigate('/host/dashboard');
  };

  const handleContactSupport = () => {
    window.open('mailto:support@bellhop.com');
  };

  return (
    <RootContainer>
      <SuccessCard>
        <CardContent sx={{ p: 6, textAlign: 'center' }}>
          {/* Success Icon */}
          <Box sx={{ mb: 4 }}>
            <CheckCircle 
              sx={{ 
                fontSize: '5rem', 
                color: '#4ade80',
                filter: 'drop-shadow(0 0 20px rgba(74, 222, 128, 0.3))'
              }} 
            />
          </Box>

          {/* Success Message */}
          <Typography 
            variant="h3" 
            sx={{ 
              color: '#fff', 
              fontWeight: 700, 
              mb: 2,
              fontSize: { xs: '2rem', md: '3rem' }
            }}
          >
            Welcome to BellHop!
          </Typography>
          
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#4ade80', 
              fontWeight: 600, 
              mb: 1 
            }}
          >
            🎉 Your Host Application Has Been Submitted!
          </Typography>
          
          <Typography 
            variant="body1" 
            sx={{ 
              color: '#b0b8c1', 
              mb: 4,
              maxWidth: 600,
              mx: 'auto'
            }}
          >
            Thank you for choosing to become a BellHop Host Partner. We've sent you an email with instructions 
            to set up your password and complete your account setup.
          </Typography>

          {/* Next Steps */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Box
                sx={{
                  background: '#2a3441',
                  border: '1px solid #3a4a5c',
                  borderRadius: 2,
                  p: 3,
                  height: '100%'
                }}
              >
                <Email sx={{ color: '#377dff', fontSize: '2.5rem', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 1 }}>
                  Check Your Email
                </Typography>
                <Typography variant="body2" sx={{ color: '#b0b8c1' }}>
                  We've sent you a confirmation email with your order details and next steps.
                </Typography>
              </Box>
            </Grid>
            
            <Grid size={{ xs: 12, md: 4 }}>
              <Box
                sx={{
                  background: 'rgba(42, 52, 65, 0.5)',
                  border: '1px solid #3a4a5c',
                  borderRadius: 2,
                  p: 3,
                  height: '100%'
                }}
              >
                <Schedule sx={{ color: '#377dff', fontSize: '2.5rem', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 1 }}>
                  Application Review
                </Typography>
                <Typography variant="body2" sx={{ color: '#b0b8c1' }}>
                  Our team will review your application within 1-2 business days.
                </Typography>
              </Box>
            </Grid>
            
            <Grid size={{ xs: 12, md: 4 }}>
              <Box
                sx={{
                  background: 'rgba(42, 52, 65, 0.5)',
                  border: '1px solid #3a4a5c',
                  borderRadius: 2,
                  p: 3,
                  height: '100%'
                }}
              >
                <Support sx={{ color: '#377dff', fontSize: '2.5rem', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600, mb: 1 }}>
                  Setup Support
                </Typography>
                <Typography variant="body2" sx={{ color: '#b0b8c1' }}>
                  Once approved, our team will help you with Botel installation and setup.
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <StyledButton
              variant="primary"
              onClick={handleGoToDashboard}
              startIcon={<Dashboard />}
              sx={{ px: 4, py: 1.5 }}
            >
              Go to Dashboard
            </StyledButton>
            
            <StyledButton
              variant="outline"
              onClick={handleContactSupport}
              startIcon={<Support />}
              sx={{ px: 4, py: 1.5 }}
            >
              Contact Support
            </StyledButton>
          </Box>

          {/* Additional Information */}
          <Box 
            sx={{ 
              mt: 4, 
              pt: 3, 
              borderTop: '1px solid #3a4a5c',
              textAlign: 'center'
            }}
          >
            <Typography variant="body2" sx={{ color: '#6b7280', mb: 2 }}>
              Questions? We're here to help!
            </Typography>
            <Typography variant="body2" sx={{ color: '#b0b8c1' }}>
              Email:{' '}
              <Typography
                component="span"
                sx={{ color: '#377dff', cursor: 'pointer' }}
                onClick={() => window.open('mailto:support@bellhop.com')}
              >
                support@bellhop.com
              </Typography>
              {' | '}
              Phone:{' '}
              <Typography
                component="span"
                sx={{ color: '#377dff', cursor: 'pointer' }}
                onClick={() => window.open('tel:+1-800-BELLHOP')}
              >
                1-800-BELLHOP
              </Typography>
            </Typography>
          </Box>
        </CardContent>
      </SuccessCard>
    </RootContainer>
  );
};

export default HostOnboardingSuccess;
